import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { OPENAI_SORA_PRO_SIZE_MAP } from "../constants";
import { attachment } from "../shared";
import { defineModel } from "../types";

const sora2ProParams = z
  .object({
    aspectRatio: z.enum(["16:9", "9:16"]).default("9:16"),
    resolution: z.enum(["720p", "1024p", "1080p"]).default("720p"),
    duration: z.enum(["4", "8", "12", "16", "20"]).default("8"),
    attachments: z.array(attachment).optional(),
    prompt: z.string().default(""),
    characterIds: z.array(z.string()).optional(),
  })
  .strip();

function getSora2ProCostPerSecond(
  resolution: z.infer<typeof sora2ProParams>["resolution"],
): number {
  const costPerSecond =
    resolution === "720p" ? 0.3 : resolution === "1024p" ? 0.5 : 0.7;

  return 1_000_000 * costPerSecond * (1 + BRAND_MARKUP);
}

export const sora2Pro = defineModel({
  key: "sora2pro",
  stepType: "video",
  spec: {
    label: "Sora 2 Pro",
    description: "Best for highly realistic people, action scenes and anime",
    icon: "sora-icon.svg",
    featured: true,
    tags: ["video-gen"],
    info: "Generating a video with this model takes between 5-10 minutes.",
    features: {
      aspectRatio: true,
      duration: true,
      resolution: true,
      characters: true,
      attachments: true,
      prompts: true,
      variations: true,
    } as const,
    options: {
      attachments: [
        {
          type: "image",
          allowedFormats: ["image/jpeg", "image/webp", "image/png"],
          label: "Input Image",
          slot: "input_image",
          required: false,
        },
      ],
      aspectRatio: {
        items: [
          { label: "9:16", value: "9:16" },
          { label: "16:9", value: "16:9" },
        ],
      },
      resolution: {
        items: [
          { label: "720p", value: "720p" },
          { label: "1024p", value: "1024p" },
          { label: "1080p", value: "1080p" },
        ],
      },
      duration: {
        items: [
          { label: "4s", value: "4" },
          { label: "8s", value: "8" },
          { label: "12s", value: "12" },
          { label: "16s", value: "16" },
          { label: "20s", value: "20" },
        ],
      },
    },
    params: sora2ProParams,
  },
  referencePricing: {
    provider: "OpenAI",
    url: "https://openai.com/api/pricing",
    entries: [
      { unit: "per_second", unitPriceMicrodollars: 300_000, variant: "720p" },
      { unit: "per_second", unitPriceMicrodollars: 500_000, variant: "1024p" },
      { unit: "per_second", unitPriceMicrodollars: 700_000, variant: "1080p" },
    ],
  },
  providers: [
    {
      provider: "openai",
      modelId: "sora-2-pro",
      estimateCost(params) {
        return Math.ceil(
          getSora2ProCostPerSecond(params.resolution) * Number(params.duration),
        );
      },
      estimateCostPerSecond(params) {
        return getSora2ProCostPerSecond(params.resolution);
      },
      costPerSecond: 0.3 * 1_000_000 * (1 + BRAND_MARKUP),
      toPayload(params) {
        const inputImageUrl = params.attachments?.find(
          (attachment) => attachment.slot === "input_image",
        )?.data;
        const size =
          OPENAI_SORA_PRO_SIZE_MAP[params.resolution][params.aspectRatio];

        return {
          model: this.modelId,
          prompt: params.prompt,
          seconds: params.duration,
          size,
          ...(inputImageUrl
            ? { input_reference: { image_url: inputImageUrl } }
            : {}),
          ...(params.characterIds && params.characterIds.length > 0
            ? {
                characters: params.characterIds.map((id) => ({
                  id,
                })),
              }
            : {}),
        };
      },
    },
  ],
});
